import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {FiSettings, FiPackage, FiLogOut, FiUser} from 'react-icons/fi';

interface AccountDropdownProps {
  isAuthenticated: boolean;
  user: any | null;
  onLogout: () => void;
  // Optional override for the trigger icon's resting-state color. Defaults
  // to `var(--text-color)`, which works fine against AdminLayout's light
  // TopBar. The dark storefront Header must pass its icon color explicitly
  // (e.g. '#FAF8F3', matching LanguageToggle) — `var(--text-color)` there
  // resolves to a near-black color that blends into the near-black header
  // background until hovered.
  color?: string;
}

const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownTrigger = styled.button<{ $color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  color: ${props => props.$color || 'var(--text-color)'};
  background: transparent;
  border: none;
  cursor: pointer;
  position: relative;

  &:hover {
    color: var(--primary-color);
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%; /* No gap to prevent mouse leaving */
  right: 0;
  background-color: var(--white);
  border: 1px solid var(--gray-light);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  margin-top: 4px; /* Visual spacing */
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: opacity 0.2s, visibility 0.2s, transform 0.2s;
  z-index: 1001;

  /* Invisible bridge to maintain hover */
  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: 0;
    right: 0;
    height: 4px;
  }

  @media (max-width: 767px) {
    right: -1rem;
  }
`;

const DropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  color: var(--text-color);
  text-decoration: none;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--gray-light);
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  color: var(--text-color);
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 1rem;
  font-family: inherit;

  &:hover {
    background-color: var(--gray-light);
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: var(--gray-light);
  margin: 0.25rem 0;
`;

const UserInfo = styled.div`
  padding: 1rem;
  border-bottom: 1px solid var(--gray-light);
`;

const UserEmail = styled.div`
  font-size: 0.875rem;
  color: var(--gray-dark);
  margin-top: 0.25rem;
`;

const AccountDropdown: React.FC<AccountDropdownProps> = ({ isAuthenticated, user, onLogout, color }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && event.target && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    // Clear any pending close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = undefined;
    }
    setIsOpen(true);
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    // Get the related target and ensure it's an Element
    const relatedTarget = e.relatedTarget;

    // If there's no related target or no dropdown ref, close after delay
    if (!dropdownRef.current) {
      closeTimeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, 300);
      return;
    }

    // Check if relatedTarget is a valid Element before using contains
    try {
      // If relatedTarget is null or the dropdown doesn't contain it, close after delay
      if (!relatedTarget ||
          !(relatedTarget instanceof Element) ||
          !dropdownRef.current.contains(relatedTarget)) {
        closeTimeoutRef.current = setTimeout(() => {
          setIsOpen(false);
        }, 300);
      }
    } catch (error) {
      // If any error occurs, close after delay
      closeTimeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, 300);
    }
  };

  const handleLogoutClick = () => {
    onLogout();
    setIsOpen(false);
  };

  // User data now comes from props, not localStorage

  return (
    <DropdownContainer
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <DropdownTrigger
        onClick={() => setIsOpen(!isOpen)}
        title={isAuthenticated ? t('accountDropdown.account') : t('accountDropdown.login')}
        $color={color}
      >
        <FiUser />
      </DropdownTrigger>

      <DropdownMenu $isOpen={isOpen}>
        {isAuthenticated ? (
          <>
            {user && (
              <UserInfo>
                <div style={{ fontWeight: 'bold' }}>
                  {user.first_name} {user.last_name}
                </div>
                <UserEmail>{user.email}</UserEmail>
              </UserInfo>
            )}
            <DropdownItem to="/account" onClick={() => setIsOpen(false)}>
              <FiSettings />
              <span>{t('accountDropdown.myAccount')}</span>
            </DropdownItem>
            <DropdownItem to="/account/orders" onClick={() => setIsOpen(false)}>
              <FiPackage />
              <span>{t('accountDropdown.myOrders')}</span>
            </DropdownItem>
            <Divider />
            <DropdownButton onClick={handleLogoutClick}>
              <FiLogOut />
              <span>{t('accountDropdown.logout')}</span>
            </DropdownButton>
          </>
        ) : (
          <>
            <DropdownItem to="/login" onClick={() => setIsOpen(false)}>
              <FiUser />
              <span>{t('accountDropdown.login')}</span>
            </DropdownItem>
            <DropdownItem to="/register" onClick={() => setIsOpen(false)}>
              <FiUser />
              <span>{t('accountDropdown.register')}</span>
            </DropdownItem>
          </>
        )}
      </DropdownMenu>
    </DropdownContainer>
  );
};

export default AccountDropdown;